import createImageBitmapFromFile from "./createImageBitmapFromFile";

export type Dimensions = {
  width: number;
  height: number;
};

// Only the file header is needed to read dimensions. JPEG SOF segments can sit
// behind several APPn blocks (EXIF thumbnails, color profiles…), so we read a
// generous window but never the whole (potentially huge) file.
const HEADER_BYTES = 256 * 1024;

const readSliceArrayBuffer = (blob: Blob): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () =>
      reject(reader.error ?? new Error("FileReader failed"));
    reader.readAsArrayBuffer(blob);
  });

const parsePng = (view: DataView): Dimensions | null => {
  if (view.byteLength < 24) {
    return null;
  }
  // PNG signature + IHDR chunk type.
  if (
    view.getUint32(0) !== 0x89504e47 ||
    view.getUint32(4) !== 0x0d0a1a0a ||
    view.getUint32(12) !== 0x49484452
  ) {
    return null;
  }
  return {
    width: view.getUint32(16),
    height: view.getUint32(20),
  };
};

const parseJpeg = (view: DataView): Dimensions | null => {
  const len = view.byteLength;
  if (len < 4 || view.getUint16(0) !== 0xffd8) {
    return null;
  }

  let offset = 2;
  while (offset + 1 < len) {
    if (view.getUint8(offset) !== 0xff) {
      offset += 1;
      continue;
    }

    let marker = view.getUint8(offset + 1);
    offset += 2;
    // Skip any fill bytes (0xFF) preceding the actual marker.
    while (marker === 0xff && offset < len) {
      marker = view.getUint8(offset);
      offset += 1;
    }

    // Standalone markers (no payload): SOI, EOI, RSTn, TEM.
    if (
      marker === 0xd8 ||
      marker === 0xd9 ||
      (marker >= 0xd0 && marker <= 0xd7) ||
      marker === 0x01
    ) {
      continue;
    }

    if (offset + 2 > len) {
      break;
    }
    const segLen = view.getUint16(offset);

    // SOF0..SOF15 carry the frame dimensions, except DHT(C4), JPG(C8), DAC(CC).
    const isStartOfFrame =
      marker >= 0xc0 &&
      marker <= 0xcf &&
      marker !== 0xc4 &&
      marker !== 0xc8 &&
      marker !== 0xcc;

    if (isStartOfFrame) {
      if (offset + 7 > len) {
        break;
      }
      return {
        height: view.getUint16(offset + 3),
        width: view.getUint16(offset + 5),
      };
    }

    offset += segLen;
  }

  return null;
};

/**
 * Read image dimensions cheaply from the file header (no full decode).
 * Falls back to a full decode only when the header can't be parsed.
 */
const readImageDimensions = async (file: File): Promise<Dimensions> => {
  try {
    const buffer = await readSliceArrayBuffer(file.slice(0, HEADER_BYTES));
    const view = new DataView(buffer);
    const fromHeader = parsePng(view) ?? parseJpeg(view);
    if (fromHeader && fromHeader.width > 0 && fromHeader.height > 0) {
      return fromHeader;
    }
  } catch {
    // ignore and fall back to a full decode below
  }

  const bitmap = await createImageBitmapFromFile(file);
  const dimensions = { width: bitmap.width, height: bitmap.height };
  bitmap.close();
  return dimensions;
};

export default readImageDimensions;
